import {decorateField, useForm, useNewRegisterField} from '@eitje/form'
import {date, formatDateRange} from '@eitje/utils'
import _ from 'lodash'
import {DateRangePicker} from '~/components/date_based_pickers'
import {DatePickerFieldTrigger} from '../root'

const DecoratedDateRangePickerField = decorateField(
	fieldProps => (
		<DateRangePicker
			{...fieldProps}
			renderTrigger={ctx => {
				const hasValue = ctx.value.length > 0
				let displayValue = fieldProps.placeholder

				if (hasValue) {
					const [startDate, endDate] = ctx.value
					const dateRange = [startDate, endDate || startDate]

					if (!fieldProps.displayFormatFn) {
						displayValue = formatDateRange(dateRange)
					} else {
						displayValue = fieldProps.displayFormatFn(dateRange)
					}
				}

				return <DatePickerFieldTrigger isPlaceholderShown={!hasValue}>{displayValue}</DatePickerFieldTrigger>
			}}
		/>
	),
	{className: 'date-picker-field', icon: true},
)

// `buildField` can only register one form field. This wrapper keeps the picker
// visually one control while storing the domain value as flat start and end fields.
export const DateRangePickerField = ({field = 'period', startField = 'start', endField = 'end', displayFormatFn, ...fieldProps}) => {
	const {form, setValues} = useForm()
	const {error: startFieldError, value: startDateValue} = useNewRegisterField(startField, {
		...fieldProps,
		field: startField,
	})
	const {error: endDateError, value: endDateValue} = useNewRegisterField(endField, {...fieldProps, field: endField})

	const handleChange = selectedDates => {
		const [startDate, endDate] = selectedDates || []
		const newValue = {[startField]: startDate?.format(), [endField]: endDate?.format()}

		if (!form?.setState) {
			setValues?.(newValue)
			return
		}

		form.setState(
			state => ({
				fields: {...state.fields, ...newValue},
				touched: true,
				touchedFields: _.uniq([...(state.touchedFields || []), ...[startField, endField]]),
			}),
			() => validateDateRangeFormFields(form, [startField, endField]),
		)
	}

	const error = startFieldError || endDateError
	const valueOverride = [startDateValue ? date(startDateValue) : undefined, endDateValue ? date(endDateValue) : undefined]

	return (
		<DecoratedDateRangePickerField
			{...fieldProps}
			field={field}
			error={error}
			value={valueOverride}
			onChange={handleChange}
			displayFormatFn={displayFormatFn}
		/>
	)
}

const validateDateRangeFormFields = (form, fields) => {
	fields.forEach(field => {
		if (!form.state.errors?.[field]) return

		const registeredField = form.state.registeredFields?.find(({fieldName}) => fieldName === field)

		form.validateField(field, true, registeredField?.props)
	})
}
